import React, { useMemo, useState } from 'react';
import { MdAdd } from 'react-icons/md';
import UniformTable from './UniformTable';

const formatTime = (ts) => {
  if (!ts) return '-';
  const s = String(ts).trim();
  if (s.length !== 14 || isNaN(Number(s))) return '-';
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  const hh = s.slice(8, 10);
  const mm = s.slice(10, 12);
  const ss = s.slice(12, 14);
  const date = new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}Z`);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

export default function Devices({ deviceRows = [], onTrack, onAddDevice }) {
  const [deviceSearch, setDeviceSearch] = useState('');

  const filteredDevices = useMemo(() => {
    const term = deviceSearch.trim().toLowerCase();
    if (!term) return deviceRows;
    return deviceRows.filter((d) => {
      return (
        String(d.name || d.deviceName || '').toLowerCase().includes(term) ||
        String(d.imei || '').toLowerCase().includes(term) ||
        String(d.address || '').toLowerCase().includes(term)
      );
    });
  }, [deviceRows, deviceSearch]);

  const dashboardDeviceTableColumns = [
    { header: '#', accessor: 'number', width: '60px', align: 'center' },
    { header: 'Name', accessor: 'name', width: '1.4fr', align: 'left' },
    { header: 'IMEI', accessor: 'imei', width: '1.8fr', align: 'left' },
    { header: 'Status', accessor: 'status', width: '1.2fr', align: 'left' },
    { header: 'Last Seen', accessor: 'lastSeen', width: '1.5fr', align: 'left' },
    { header: 'Action', accessor: 'action', width: '220px', align: 'center' }
  ];

  const dashboardDeviceTableRows = filteredDevices.map((d, i) => ({
    number: (
      <span className="devices-badge fleet-table-index">{String(i + 1).padStart(2, '0')}</span>
    ),
    name: d.name || d.deviceName || 'Device',
    imei: <span className="mono">{d.imei || '-'}</span>,
    status: (
      <span
        className={`devices-status-text ${d.status?.toLowerCase() === 'running'
          ? 'running'
          : d.status?.toLowerCase() === 'idle'
            ? 'idle'
            : 'other'
          }`}
      >
        {d.status || '-'}
      </span>
    ),
    lastSeen: formatTime(d.timestamp || d.lastUpdate),
    action: (
      <div className="devices-action-group">
        <button className="btn-details" onClick={() => onTrack?.(d.imei, 'details')}>
          Details
        </button>
        <button className="btn-track" onClick={() => onTrack?.(d.imei, 'track')}>
          Track
        </button>
      </div>
    )
  }));

  return (
  <div className="devices-container">
    {/* ── Desktop view ────────────────────────────────────────────── */}
    <div className="devices-desktop">
      {/* SEARCH BAR – sticky (handled by CSS) */}
      <div className="devices-header-row">
        <div className="devices-page-heading">
          <h1>Devices</h1>
          <span>{deviceRows.length} registered</span>
        </div>
        <div className="devices-header-actions">
        <input
          className="devices-search-input"
          type="text"
          placeholder="Search name, IMEI or address…"
          value={deviceSearch}
          onChange={e => setDeviceSearch(e.target.value)}
        />
          <button className="devices-add-button" type="button" onClick={onAddDevice}>
            <MdAdd aria-hidden="true" />
            <span>Add Device</span>
          </button>
        </div>
      </div>

      {/* TABLE wrapper – the UniformTable component now contains its own
          scrollable container (`.table-responsive`) and sticky header. */}
      <div className="devices-table-container fleet-table-wrapper">
        {dashboardDeviceTableRows.length === 0 ? (
          <div className="align-center fleet-table-empty">No devices found</div>
        ) : (
          <UniformTable
            columns={dashboardDeviceTableColumns}
            data={dashboardDeviceTableRows}
          />
        )}
      </div>
    </div>

    {/* ── Mobile view ─────────────────────────────────────────────── */}
    <div className="devices-mobile">
      <div className="devices-header-row">
        <div className="devices-page-heading">
          <h1>Devices</h1>
          <span>{deviceRows.length} registered</span>
        </div>
        <div className="devices-header-actions">
        <input
          className="devices-search-input"
          type="text"
          placeholder="Search name, IMEI or address…"
          value={deviceSearch}
          onChange={e => setDeviceSearch(e.target.value)}
        />
          <button className="devices-add-button" type="button" onClick={onAddDevice}>
            <MdAdd aria-hidden="true" />
            <span>Add Device</span>
          </button>
        </div>
      </div>

      <div className="mobile-devices-container">
        {filteredDevices.length === 0 ? (
          <div
            className="align-center"
            style={{
              padding: "24px",
              color: "#64748b",
              background: "#f8fafc",
              borderRadius: "8px"
            }}
          >
            No devices found
          </div>
        ) : (
          filteredDevices.map((d, i) => (
            <div key={d.imei || d.id || i} className="mobile-device-card">
              <div className="mobile-device-card-header">
                <h3 className="mobile-device-name">
                  {d.name || d.deviceName || "Device"}
                </h3>
                <span className="devices-badge">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              <div className="mobile-device-card-body">
                <div className="mobile-device-info-row">
                  <span className="mobile-info-label">IMEI</span>
                  <span className="mobile-info-value mono">
                    {d.imei || "-"}
                  </span>
                </div>

                <div className="mobile-device-info-row">
                  <span className="mobile-info-label">Status</span>
                  <span
                    className={`devices-status-text ${
                      d.status?.toLowerCase() === "running"
                        ? "running"
                        : d.status?.toLowerCase() === "idle"
                        ? "idle"
                        : "other"
                    }`}
                  >
                    {d.status || "-"}
                  </span>
                </div>

                <div className="mobile-device-info-row">
                  <span className="mobile-info-label">Last Seen</span>
                  <span className="mobile-info-value">
                    {formatTime(d.timestamp || d.lastUpdate)}
                  </span>
                </div>
              </div>

              <div className="mobile-device-actions">
                <button
                  className="btn-details"
                  onClick={() => onTrack?.(d.imei, "details")}
                >
                  Details
                </button>
                <button
                  className="btn-track"
                  onClick={() => onTrack?.(d.imei, "track")}
                >
                  Track
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);
}